// Build action bar — bottom-center row of 6 build actions (Farm, Road,
// Wall, Pillbox, Mine, Boat), deliberately compact so it clears the touch
// zones either side of it (virtual stick: bottom-left quadrant; fire/mine:
// bottom-right corner). Tapping a button arms the action via
// ctx.armBuildAction(); the armed state re-reads ctx.armedBuildAction()
// every frame so the highlight stays in sync no matter who armed it.
//
// A small status chip rides above the bar: the LGM indicator (dot + label —
// builds queue while the LGM is out of the tank) plus a "tap map" hint while
// an action is armed.
//
// Icons reuse the desktop art (/sprites/hud/actions/*.png, synced into
// client3k/public by sync-assets). There is no boat.png — the desktop
// sidebar predates BuildAction.Boat — so the boat cell uses an inline SVG
// data URI (CSS-only art, no new binary asset) drawn in the same 16px
// pixel grid and phosphor green as the sprite set. Each cell also carries a
// 4-char stencil code label (.m3k-action-label) under its icon, styled in
// mobileHud.css.
//
// Tap-again-to-disarm is intentionally absent: GameContext.armBuildAction is
// typed (action: BuildActionCode) with no null — disarming needs a context
// change (integration ask). Until then, re-tapping the armed action is a
// no-op, matching the desktop ActionSidebar's radio-button semantics.

import { BuildAction, LGMStatus, type BuildActionCode } from '@bolo2k/sim';
import type { GameContext } from '../context.js';

export interface BuildBar {
  readonly el: HTMLElement;
  /** Sync armed highlight + LGM/hint chip. Called once per frame. */
  update(armed: BuildActionCode | null, lgmStatus: number | null): void;
}

interface Slot {
  action: BuildActionCode;
  label: string;
  /** 4-char stencil code shown under the icon (fits 46px cells at 12px). */
  code: string;
  iconUrl?: string;
  glyph?: string;
}

/** Pixel-art boat (16px grid, phosphor green) as an inline SVG data URI —
 *  stands in for the missing boat.png without adding a binary asset. */
const BOAT_ICON =
  "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' shape-rendering='crispEdges' fill='%239fdf9f'><rect x='7' y='1' width='1' height='10'/><path d='M8 2h1v1h1v1h1v1h1v1h1v2H8z'/><rect x='2' y='11' width='12' height='1'/><rect x='3' y='12' width='10' height='1'/><rect x='4' y='13' width='8' height='1'/></svg>";

const SLOTS: ReadonlyArray<Slot> = [
  { action: BuildAction.Farm, label: 'Farm', code: 'FARM', iconUrl: '/sprites/hud/actions/farm.png' },
  { action: BuildAction.Road, label: 'Road', code: 'ROAD', iconUrl: '/sprites/hud/actions/road.png' },
  { action: BuildAction.Building, label: 'Wall', code: 'WALL', iconUrl: '/sprites/hud/actions/building.png' },
  { action: BuildAction.Pillbox, label: 'Pillbox', code: 'PBOX', iconUrl: '/sprites/hud/actions/pillbox.png' },
  { action: BuildAction.Mine, label: 'Mine', code: 'MINE', iconUrl: '/sprites/hud/actions/mine.png' },
  { action: BuildAction.Boat, label: 'Boat', code: 'BOAT', iconUrl: BOAT_ICON },
];

// LGM status → chip label + dot colour class. Codes from @bolo2k/sim
// LGMStatus (types.ts): InTank 0, WalkingTo 1, Building 2, WalkingHome 3,
// Dead 4 (transient), Parachuting 5.
const LGM_VIEW: Record<number, { label: string; cls: string }> = {
  [LGMStatus.InTank]: { label: 'LGM ready', cls: 'ok' },
  [LGMStatus.WalkingTo]: { label: 'LGM out', cls: 'busy' },
  [LGMStatus.Building]: { label: 'LGM building', cls: 'busy' },
  [LGMStatus.WalkingHome]: { label: 'LGM returning', cls: 'busy' },
  [LGMStatus.Dead]: { label: 'LGM down', cls: 'down' },
  [LGMStatus.Parachuting]: { label: 'LGM parachuting', cls: 'down' },
};
const LGM_CLASSES = ['m3k-lgm--ok', 'm3k-lgm--busy', 'm3k-lgm--down'];

export function createBuildBar(ctx: GameContext): BuildBar {
  // Outer flex column (status chip above, button row below), centered at the
  // bottom edge by .m3k-buildwrap.
  const el = document.createElement('div');
  el.className = 'm3k-buildwrap';

  const lgmChip = document.createElement('div');
  lgmChip.className = 'm3k-chip m3k-lgm';
  const dot = document.createElement('span');
  dot.className = 'm3k-lgm-dot';
  const lgmText = document.createElement('span');
  lgmText.textContent = 'LGM';
  lgmChip.append(dot, lgmText);

  const bar = document.createElement('div');
  bar.className = 'm3k-chip m3k-buildbar';

  const cells: Array<{ btn: HTMLButtonElement; action: BuildActionCode }> = [];
  for (const slot of SLOTS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'm3k-action';
    btn.title = slot.label;
    btn.setAttribute('aria-label', slot.label);
    if (slot.iconUrl) btn.style.backgroundImage = `url("${slot.iconUrl}")`;
    if (slot.glyph) btn.textContent = slot.glyph;
    const codeLabel = document.createElement('span');
    codeLabel.className = 'm3k-action-label';
    codeLabel.textContent = slot.code;
    btn.appendChild(codeLabel);
    btn.addEventListener('click', () => {
      // Re-tapping the armed action stays a no-op until GameContext grows a
      // null/disarm path (see header comment).
      if (ctx.armedBuildAction() === slot.action) return;
      ctx.armBuildAction(slot.action);
    });
    bar.appendChild(btn);
    cells.push({ btn, action: slot.action });
  }

  el.append(lgmChip, bar);

  let lastArmed: BuildActionCode | null | undefined;
  let lastLgm: number | null | undefined;

  function update(armed: BuildActionCode | null, lgmStatus: number | null): void {
    if (armed === lastArmed && lgmStatus === lastLgm) return;
    lastArmed = armed;
    lastLgm = lgmStatus;

    for (const cell of cells) {
      cell.btn.classList.toggle('m3k-action--armed', cell.action === armed);
    }

    const view = lgmStatus !== null ? LGM_VIEW[lgmStatus] : undefined;
    lgmChip.classList.remove(...LGM_CLASSES);
    if (view) lgmChip.classList.add(`m3k-lgm--${view.cls}`);

    if (armed !== null) {
      const slot = SLOTS.find((s) => s.action === armed);
      const busy = view && view.cls !== 'ok' ? ' (queues — LGM busy)' : '';
      lgmText.textContent = `${slot?.label ?? '?'}: tap map${busy}`;
    } else {
      lgmText.textContent = view?.label ?? 'LGM —';
    }
  }

  return { el, update };
}
